/* trace-link.js — resolve a Developer Console trace line to a source target.
 *
 * IronPython compiles scripts from a string, so error tracebacks read
 * `File "<string>", line N` — the *file* identity isn't on the error line, it's
 * on a sibling/ancestor `Script Execution - X` or `from X import` line. This
 * resolver walks the hierarchy to recover (scriptName, line), preferring a
 * named module over a descriptive event-script name (which has no file and
 * falls back to Find-in-Files). Returns null cleanly when nothing matches —
 * never a broken link.
 *
 * Pure (no imports) so it's unit-tested in Node; the DOM opener lives in traces.js.
 */

const _BARE_RE = /Script Execution\s*-\s*([A-Za-z_]\w*)\s*$/;
const _IMPORT_RE = /\bfrom\s+([A-Za-z_]\w*)\s+import\b/;
const _DESC_RE = /Script Execution\s*-\s*(.+\(.+)/; // descriptive name (has a paren)
const _STRING_LINE_RE = /File\s+"<string>",\s*line\s+(\d+)/g;

function _hitFromMsg(msg) {
  if (!msg) return null;
  let m = _BARE_RE.exec(msg);
  if (m) return { mode: "open", scriptName: m[1] };
  m = _IMPORT_RE.exec(msg);
  if (m) return { mode: "open", scriptName: m[1] };
  m = _DESC_RE.exec(msg);
  if (m) return { mode: "search", query: m[1].trim() };
  return null;
}

function _lastStringLine(text) {
  if (!text) return null;
  let last = null;
  let m;
  _STRING_LINE_RE.lastIndex = 0;
  while ((m = _STRING_LINE_RE.exec(text)) !== null) last = parseInt(m[1], 10);
  return last;
}

/**
 * @param {object} line — trace line {message, full_message, hierarchy_id, ...}
 * @param {Array}  allLines — all lines in the action (for ancestor lookup)
 * @returns {{mode:'open', scriptName:string, line:?number}
 *          | {mode:'search', query:string} | null}
 */
export function resolveTraceTarget(line, allLines) {
  if (!line) return null;
  const lineNo = _lastStringLine(`${line.message || ""}\n${line.full_message || ""}`);

  let hit = _hitFromMsg(line.message) || _hitFromMsg(line.full_message);

  // If the line itself didn't name a file (or only a descriptive one), walk up
  // its hierarchy_id, preferring a named module ('open') over a descriptive
  // event-script name ('search').
  if ((!hit || hit.mode === "search") && line.hierarchy_id && Array.isArray(allLines)) {
    const parts = String(line.hierarchy_id).split(".");
    let searchHit = hit && hit.mode === "search" ? hit : null;
    let openHit = null;
    for (let i = parts.length - 1; i > 0 && !openHit; i--) {
      const prefix = parts.slice(0, i).join(".");
      const anc = allLines.find((l) => String(l.hierarchy_id) === prefix);
      if (!anc) continue;
      const h = _hitFromMsg(anc.message) || _hitFromMsg(anc.full_message);
      if (h && h.mode === "open") openHit = h;
      else if (h && h.mode === "search" && !searchHit) searchHit = h;
    }
    hit = openHit || searchHit || hit;
  }

  if (!hit) return null;
  if (hit.mode === "open") return { mode: "open", scriptName: hit.scriptName, line: lineNo };
  return { mode: "search", query: hit.query };
}
